"use client";

import { useEffect, useState } from "react";

import FeedbackCard from "@/components/sections/Guestbook/FeedbackCard";
import Title from "@/components/shared/Title";
import { useTranslation } from "@/hooks/useTranslation";
import getFeedbacksPagination from "@/lib/guestbook/getFeedbacksPagination";
import type { Feedback } from "@/payload-types";
import type { PaginationPayload } from "@/types";

export default function FeedbackList() {
  const [page, setPage] = useState(1);
  const [feedbackData, setFeedbackData] =
    useState<PaginationPayload<Feedback>>();

  const { t } = useTranslation();
  useEffect(() => {
    getFeedbacksPagination(page).then((data) => {
      if (!data) return;
      setFeedbackData(data);
    });
  }, [page]);

  const nextPage = () => setPage(page + 1);
  const prevPage = () => setPage(page - 1);

  const changePage = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="bg-white px-5 py-40">
      <Title
        title={t({
          de: "Das sagen unsere Gäste",
          en: "What Our Guests Say",
        })}
        desc={t({
          de: "Echte Geschichten und ehrliche Erlebnisse unserer Gäste.",
          en: "Real stories and honest experiences from our guests.",
        })}
      />
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="text-gray-600">
            {t({ de: "Anzeige: ", en: "Displaying results " })}
            <span className="font-semibold">
              {feedbackData?.totalDocs ? 8 * (page - 1) + 1 : 0} to{" "}
              {Math.min(page * 8, feedbackData?.totalDocs || 0)}
            </span>{" "}
            {t({ en: "out of ", de: "von " })}
            <span className="font-semibold">{feedbackData?.totalDocs}</span>
          </div>
          <div className="flex space-x-2">
            {feedbackData?.hasPrevPage && (
              <span
                className="cursor-pointer rounded px-2 py-1 text-primary-600 underline hover:bg-blue-100"
                onClick={prevPage}
              >
                {t({ en: "Prev", de: "< Vorherige Einträge" })}
              </span>
            )}
            {feedbackData?.prevPage && (
              <span
                className="cursor-pointer rounded px-2 py-1 text-primary-600 hover:bg-blue-100"
                onClick={() =>
                  feedbackData?.prevPage && changePage(feedbackData?.prevPage)
                }
              >
                {feedbackData?.prevPage}
              </span>
            )}
            <span className="rounded bg-primary-500 px-2 py-1 text-white">
              {page}
            </span>
            {feedbackData?.nextPage && (
              <span
                className="cursor-pointer rounded px-2 py-1 text-primary-600 hover:bg-blue-100"
                onClick={() =>
                  feedbackData?.nextPage && changePage(feedbackData?.nextPage)
                }
              >
                {feedbackData?.nextPage}
              </span>
            )}
            {feedbackData?.hasNextPage && (
              <span
                className="cursor-pointer rounded px-2 py-1 text-primary-600 underline hover:bg-blue-100"
                onClick={nextPage}
              >
                {t({ en: "Next", de: "Weitere Einträge >" })}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-5">
          {(feedbackData?.docs || []).map((feedback) => (
            <FeedbackCard key={feedback.id} feedback={feedback} />
          ))}
        </div>
      </div>
    </div>
  );
}
